/**
 * SDK Factory — createSdk() entry point (§80 §5.4)
 *
 * Accepts a config-connection-token-v2 (§4.3) or direct workspace-secret-v2 params.
 * The connection token is parsed → config-blob is read via workspace auth → workspaces are listed/opened.
 *
 * This is the primary public API for SDK consumers.
 */

import { sha256 as nobleSha256 } from '@noble/hashes/sha2'
import { hkdf as nobleHkdf } from '@noble/hashes/hkdf'
import type {
    ContentRef,
    CryptoProvider,
    SyncScheduler,
} from './types'
import type { WorkspaceSqliteEngineFactoryProvider, Sqlite3MtOptions } from './sqliteEngine'
import type { SdkObservers } from './observers'
import { configureSdkLogging, type SdkLoggingOptions } from './logger'
import { SdkError } from './errors'
import { RealWorkspaceSession, type WorkspaceSession } from './session'
import { openSdkWorkspace } from './realWorkspace'
import { WebCryptoProvider, HttpRemoteStore } from './providers'
import { buildAad } from './codec/state'
import { resolveContentBytes } from './codec/content'
import { createWorkspaceAuthProvider } from './workspaceAuth'
import {
    decodeConfigConnectionTokenV2,
    decodeWorkspaceSecretV2,
    type WorkspaceSecretV2,
} from './workspaceSecretV2'

// ── MCP config types (mirrored from webapp/mcp/types.ts) ─────────

/** Config-blob owner metadata */
interface McpConfigOwner {
    primaryKid: string
    displayName: string
    emoji?: string
}

/** Root config object stored as /config.json in a config SecureBlob */
interface McpConfig {
    v: 1
    type: 'secureblob-mcp-config'
    label: string
    owner: McpConfigOwner
    workspaces: McpWorkspaceConfig[]
    defaults?: McpDefaults
}

/** Per-workspace config inside config-blob */
interface McpWorkspaceConfig {
    blobId: string
    alias?: string
    title: string
    emoji?: string
    workspaceSecret: string
    permissions: McpPermissions
    limits?: McpLimits
}

/** Permission flags per workspace */
interface McpPermissions {
    read: boolean
    write: boolean
    rename: boolean
    delete: boolean
    allowBinaryRead: boolean
}

interface McpLimits {
    maxReadBytes?: number
    maxWriteBytes?: number
    maxSearchMatches?: number
}

interface McpDefaults {
    maxReadBytes?: number
    maxWriteBytes?: number
    maxSearchMatches?: number
    allowBinaryRead?: boolean
}

/** Parsed config-connection-token-v2 with decoded workspace secret. */
interface ParsedConnectionToken {
    apiBaseUrl: string
    secret: string
    decodedSecret: WorkspaceSecretV2
}

// ── Public SDK types ──────────────────────────────────────────────

export interface SdkConfig {
    /** API endpoint. Default: 'https://secureblob.hoglundit.se' */
    apiBaseUrl?: string

    /** Connection token from MCP/config-blob flow.
     *  Format: config-connection-token-v2.
     *  Required for listWorkspaces() and openWorkspace(alias). */
    token?: string

    /** Custom crypto provider (default: WebCryptoProvider). */
    crypto?: CryptoProvider
    /** Custom scheduler (default: DebounceSyncScheduler). */
    scheduler?: SyncScheduler
    /**
     * Optional SQLite engine-factory provider for all workspaces opened through
     * this SDK instance. This is the public injection seam for sqlite3mt.
     */
    createSqliteEngineFactory?:
        WorkspaceSqliteEngineFactoryProvider['createSqliteEngineFactory']
    /**
     * sqlite3mt multi-worker SQLite configuration.
     *
     * sqlite3mt is the production default. Pass an options object to customize
     * page size, persistence mode, etc.
     */
    useSqlite3Mt?: Sqlite3MtOptions
    /** Optional SDK-wide observer hooks for workspace, SQLite, and MCP surfaces. */
    observers?: SdkObservers
    /** Optional console logging control. Default console level is `debug`. */
    logging?: SdkLoggingOptions
}

export interface SdkWorkspaceInfo {
    blobId: string
    alias?: string
    title: string
    emoji?: string
    canWrite: boolean
    permissions: McpPermissions
}

export interface OpenOptions {
    conflictResolution?: 'auto' | 'manual'
    pollIntervalMs?: number
    persistence?: 'idb' | 'memory'
}

export interface DirectWorkspaceParams {
    apiBaseUrl: string
    workspaceSecret: string
    options?: OpenOptions
}

export interface SecureBlobSdk {
    /** List all workspaces in the connected MCP config-blob. */
    listWorkspaces(): Promise<SdkWorkspaceInfo[]>
    /** Open a workspace by blobId or alias (from config-blob). */
    openWorkspace(target: string, options?: OpenOptions): Promise<WorkspaceSession>
    /** Open a workspace with an explicit workspace-secret-v2 (no config-blob). */
    openWorkspaceDirect(params: DirectWorkspaceParams): Promise<WorkspaceSession>
}

// ── Helpers ───────────────────────────────────────────────────────

const DEFAULT_API_BASE_URL = 'https://secureblob.hoglundit.se/api/cryptoblobstore'

function base64UrlDecode(str: string): string {
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/')
    while (base64.length % 4) base64 += '='
    return atob(base64)
}

function base64UrlDecodeBytes(str: string): Uint8Array {
    const binary = base64UrlDecode(str)
    return new Uint8Array([...binary].map(c => c.charCodeAt(0)))
}

function parseConnectionToken(token: string): ParsedConnectionToken {
    const parsed = decodeConfigConnectionTokenV2(token)
    if (!parsed) {
        throw new SdkError('INVALID_TOKEN', 'Invalid connection token: expected config-connection-token-v2.')
    }

    const secret = decodeWorkspaceSecretV2(parsed.secret)
    if (!secret) {
        throw new SdkError('INVALID_TOKEN', 'Invalid connection token: embedded workspace secret is malformed.')
    }

    return {
        apiBaseUrl: parsed.apiBaseUrl,
        secret: parsed.secret,
        decodedSecret: secret,
    }
}

function deriveKey(readKey: string, blobId: string, scope: 'state' | 'file'): Uint8Array {
    const readKeyBytes = base64UrlDecodeBytes(readKey)
    const salt = new TextEncoder().encode(`secureblob:${blobId}`)
    const info = new TextEncoder().encode(`secureblob-${scope}-v1`)
    return nobleHkdf(nobleSha256, readKeyBytes, salt, info, 32)
}

// ── Config-blob reader ────────────────────────────────────────────

async function readConfigBlob(
    apiBaseUrl: string,
    encodedSecret: string,
    secret: WorkspaceSecretV2,
    crypto: CryptoProvider,
): Promise<McpConfig> {
    const authProvider = createWorkspaceAuthProvider(apiBaseUrl, encodedSecret, secret.blobId) ?? undefined
    const remote = new HttpRemoteStore(apiBaseUrl, encodedSecret, secret.blobId, authProvider)

    // 1. Head → commit → stateHash
    const { heads } = await remote.getHeads(secret.blobId)

    if (!heads || heads.length === 0) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Config-blob has no head commit')
    }

    const commitBytes = await remote.getObject(heads[0])
    if (!commitBytes) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Could not fetch config-blob commit')
    }


    const commit = JSON.parse(new TextDecoder().decode(commitBytes))
    const stateHash: string | undefined = commit.payload?.stateHash

    if (!stateHash) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Config-blob commit has no payload.stateHash')
    }

    // 2. Fetch + decrypt state (uses spec §4.3 AAD via buildAad)
    const stateKey = deriveKey(secret.readKey, secret.blobId, 'state')

    const stateCiphertext = await remote.getObject(stateHash)
    if (!stateCiphertext) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Could not fetch config-blob state object')
    }


    const stateAad = buildAad(secret.blobId, 'state')
    const statePlaintext = await crypto.decrypt(stateKey, stateCiphertext, stateAad)
    const state = JSON.parse(new TextDecoder().decode(statePlaintext))

    // 3. v3 state: walk Merkle tree to find /config.json
    const rootDirHash: string | undefined = state.rootDirHash
    if (!rootDirHash) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Config-blob state has no rootDirHash (empty workspace)')
    }

    // Fetch + decrypt root directory object
    const dirAad = buildAad(secret.blobId, 'dir')
    const dirCiphertext = await remote.getObject(rootDirHash)
    if (!dirCiphertext) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Could not fetch config-blob root directory object')
    }
    const dirPlaintext = await crypto.decrypt(stateKey, dirCiphertext, dirAad)
    const dirObj = JSON.parse(new TextDecoder().decode(dirPlaintext))

    // Find config.json entry in root directory
    const configEntry = dirObj.entries?.find((e: { name: string; kind: string }) => e.name === 'config.json' && e.kind === 'file')
    if (!configEntry) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Config-blob root directory has no config.json')
    }

    // 4. Decrypt /config.json content (inline or object-referenced)
    const fileKey = deriveKey(secret.readKey, secret.blobId, 'file')
    const fileAad = buildAad(secret.blobId, 'file')

    const content = configEntry.content as ContentRef | undefined
    if (!content || (content.type !== 'inline' && content.type !== 'chunked')) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Config-blob config.json has no recognizable content reference')
    }

    const configBytes = await resolveContentBytes(
        content,
        {
            load: async (hash) => remote.getObject(hash),
            loadBatch: async (hashes) => {
                const entries = await Promise.all(
                    hashes.map(async hash => [hash, await remote.getObject(hash)] as const),
                )
                return new Map(entries.filter((entry): entry is readonly [string, Uint8Array] => entry[1] !== null))
            },
        },
        {
            decryptRawObject: (objectBytes) => crypto.decrypt(fileKey, objectBytes, fileAad),
            // Required by ContentCodecDeps for write-paths; not used during resolve.
            encryptRawObject: async () => { throw new Error('Not implemented in readConfigBlob') },
            sha256: async () => { throw new Error('Not implemented in readConfigBlob') },
            encryptDeterministic: async () => { throw new Error('Not implemented in readConfigBlob') },
            hasObject: async () => false,
        },
    )
    if (!configBytes) {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', 'Could not resolve config.json content objects')
    }

    const config = JSON.parse(new TextDecoder().decode(configBytes))
    if (config.type !== 'secureblob-mcp-config') {
        throw new SdkError('CONFIG_BLOB_UNAVAILABLE', `Unexpected config type: ${config.type}`)
    }

    return config as McpConfig
}

// ── Factory implementation ────────────────────────────────────────

class RealSecureBlobSdk implements SecureBlobSdk {
    private readonly apiBaseUrl: string
    private readonly tokenPayload: ParsedConnectionToken | null
    private readonly crypto: CryptoProvider
    private readonly scheduler: SyncScheduler | undefined
    private readonly createSqliteEngineFactory:
        | WorkspaceSqliteEngineFactoryProvider['createSqliteEngineFactory']
        | undefined
    private readonly useSqlite3Mt: Sqlite3MtOptions | undefined
    private readonly observers: SdkObservers | undefined
    private readonly logging: SdkLoggingOptions | undefined
    private cachedConfig: McpConfig | null = null

    constructor(config: SdkConfig) {
        configureSdkLogging(config.logging)
        this.tokenPayload = config.token ? parseConnectionToken(config.token) : null
        this.apiBaseUrl = config.apiBaseUrl ?? this.tokenPayload?.apiBaseUrl ?? DEFAULT_API_BASE_URL
        this.crypto = config.crypto ?? new WebCryptoProvider()
        this.scheduler = config.scheduler
        this.createSqliteEngineFactory = config.createSqliteEngineFactory
        this.useSqlite3Mt = config.useSqlite3Mt
        this.observers = config.observers
        this.logging = config.logging
    }

    async listWorkspaces(): Promise<SdkWorkspaceInfo[]> {
        const config = await this.ensureConfig()
        return config.workspaces.map(ws => ({
            blobId: ws.blobId,
            alias: ws.alias,
            title: ws.title,
            emoji: ws.emoji,
            canWrite: ws.permissions.write,
            permissions: ws.permissions,
        }))
    }

    async openWorkspace(target: string, options?: OpenOptions): Promise<WorkspaceSession> {
        const config = await this.ensureConfig()

        // Match by alias or blobId
        const wsConfig = config.workspaces.find(
            ws => ws.alias === target || ws.blobId === target,
        )
        if (!wsConfig) {
            throw new SdkError('CONFIG_BLOB_UNAVAILABLE', `Workspace '${target}' not found in config-blob`)
        }

        return this.openFromConfig(wsConfig, options)
    }

    async openWorkspaceDirect(params: DirectWorkspaceParams): Promise<WorkspaceSession> {
        const secret = decodeWorkspaceSecretV2(params.workspaceSecret)
        if (!secret) {
            throw new SdkError('INVALID_TOKEN', 'Direct workspace open requires a valid workspace-secret-v2.')
        }

        return this.createSession({
            apiBaseUrl: params.apiBaseUrl,
            blobId: secret.blobId,
            workspaceSecret: params.workspaceSecret,
            options: params.options,
        })
    }

    // ── Private helpers ──────────────────────────────────────────

    private async ensureConfig(): Promise<McpConfig> {
        if (this.cachedConfig) return this.cachedConfig

        if (!this.tokenPayload) {
            throw new SdkError('CONFIG_BLOB_UNAVAILABLE',
                'No connection token provided. Use openWorkspaceDirect() with a workspace secret for ad-hoc access.')
        }

        this.cachedConfig = await readConfigBlob(
            this.apiBaseUrl,
            this.tokenPayload.secret,
            this.tokenPayload.decodedSecret,
            this.crypto,
        )

        return this.cachedConfig
    }

    private async openFromConfig(wsConfig: McpWorkspaceConfig, options?: OpenOptions): Promise<WorkspaceSession> {
        return this.createSession({
            apiBaseUrl: this.apiBaseUrl,
            blobId: wsConfig.blobId,
            workspaceSecret: wsConfig.workspaceSecret,
            options,
        })
    }

    private async createSession(params: {
        apiBaseUrl: string
        blobId: string
        workspaceSecret: string
        options?: OpenOptions
    }): Promise<WorkspaceSession> {
        const { apiBaseUrl, blobId, workspaceSecret } = params

        const secret = decodeWorkspaceSecretV2(workspaceSecret)
        if (!secret) {
            throw new SdkError('INVALID_TOKEN', 'Workspace config entry is not a valid workspace-secret-v2.')
        }
        if (secret.blobId !== blobId) {
            throw new SdkError('INVALID_TOKEN', `Workspace secret blobId '${secret.blobId}' does not match requested blobId '${blobId}'.`)
        }

        // WorkspaceEntry is still named "capability" internally, but v2 callers now pass
        // the encoded workspace-secret-v2 string through that field until the wider rename lands.
        const entry = {
            blobId,
            label: blobId.slice(0, 8),
            emoji: '📂',
            apiBaseUrl,
            capability: workspaceSecret,
        }

        // Create + initialize workspace in one clean async call
        const workspace = await openSdkWorkspace(
            entry,
            'sdk-user',
            this.createSqliteEngineFactory,
            this.useSqlite3Mt,
            this.observers,
            this.logging,
        )

        return new RealWorkspaceSession(workspace)
    }
}

// ── Public factory ────────────────────────────────────────────────

/**
 * Create an SDK instance.
 *
 * With token (config-connection-token-v2):
 *   const sdk = createSdk({ token: 'eyJ...' })
 *   const workspaces = await sdk.listWorkspaces()
 *   const ws = await sdk.openWorkspace('projekt-x')
 *
 * Without token (direct workspace secret):
 *   const sdk = createSdk({ apiBaseUrl: 'https://...' })
 *   const ws = await sdk.openWorkspaceDirect({ ... })
 */
export function createSdk(config: SdkConfig = {}): SecureBlobSdk {
    return new RealSecureBlobSdk(config)
}
